(() => {
  const SELECTOR = '[data-main-pdp-swiper]';
  const BOUND_ATTR = 'data-pdp-gallery-video-bound';

  const isVideoSlide = (slide) => slide?.dataset?.mediaType === 'video';

  function prepareVideo(video) {
    if (!video) return;

    video.muted = true;
    video.defaultMuted = true;
    video.playsInline = true;
    video.setAttribute('playsinline', 'playsinline');
    video.preload = 'metadata';
    video.controls = false;
    video.removeAttribute('controls');
  }

  function loadDeferredVideo(slide) {
    const deferred = slide?.querySelector('deferred-media');
    if (!deferred) return null;

    if (!deferred.hasAttribute('loaded')) {
      if (typeof deferred.loadContent === 'function') {
        deferred.loadContent(false);
      } else {
        const template = deferred.querySelector('template');
        const media = template?.content?.firstElementChild?.cloneNode(true);

        if (media) {
          const content = document.createElement('div');
          content.appendChild(media);
          deferred.setAttribute('loaded', true);
          deferred.appendChild(content.querySelector('video, iframe'));
        }
      }
    }

    const video = deferred.querySelector('video');
    prepareVideo(video);
    return video;
  }

  function pauseInactiveVideos(slider, activeSlide) {
    slider.querySelectorAll('video').forEach((video) => {
      if (!activeSlide || !activeSlide.contains(video)) {
        video.pause();
      }
    });
  }

  function prewarmAdjacentVideo(swiper) {
    const activeSlide = swiper.slides[swiper.activeIndex];

    // Do not call deferred-media.loadContent while the active slide is a video,
    // because loadContent pauses all media before inserting the video element.
    if (isVideoSlide(activeSlide)) return;

    [swiper.activeIndex - 1, swiper.activeIndex + 1].forEach((index) => {
      const slide = swiper.slides[index];
      if (!isVideoSlide(slide)) return;

      const video = loadDeferredVideo(slide);
      if (video) video.pause();
    });
  }

  function playActiveVideo(swiper) {
    const activeSlide = swiper.slides[swiper.activeIndex];
    pauseInactiveVideos(swiper.el, activeSlide);

    if (!isVideoSlide(activeSlide)) {
      prewarmAdjacentVideo(swiper);
      return;
    }

    const video = loadDeferredVideo(activeSlide);
    if (!video) return;

    video.play().catch(() => {
      // Browser autoplay policies can still block playback in edge cases.
      // In that case the poster remains usable and the user can tap the slide.
    });
  }

  function onPosterClick(event) {
    const poster = event.target.closest('.deferred-media__poster');
    if (!poster || !event.currentTarget.contains(poster)) return;

    const slide = poster.closest('.swiper-slide');
    if (!isVideoSlide(slide)) return;

    window.setTimeout(() => {
      const video = slide.querySelector('video');
      prepareVideo(video);
      video?.play().catch(() => {});
    }, 0);
  }

  function bindSlider(slider) {
    if (slider.hasAttribute(BOUND_ATTR)) return;

    const attach = () => {
      const swiper = slider.swiper;
      if (!swiper || typeof swiper.on !== 'function') return false;

      slider.setAttribute(BOUND_ATTR, 'true');
      slider.addEventListener('click', onPosterClick);

      swiper.on('slideChange', () => {
        const activeSlide = swiper.slides[swiper.activeIndex];
        pauseInactiveVideos(slider, activeSlide);
        prewarmAdjacentVideo(swiper);
      });

      swiper.on('slideChangeTransitionEnd', () => playActiveVideo(swiper));
      swiper.on('transitionEnd', () => playActiveVideo(swiper));

      window.requestAnimationFrame(() => {
        playActiveVideo(swiper);
        prewarmAdjacentVideo(swiper);
      });

      return true;
    };

    if (attach()) return;

    let attempts = 0;
    const timer = window.setInterval(() => {
      attempts += 1;
      if (attach() || attempts > 50) {
        window.clearInterval(timer);
      }
    }, 100);
  }

  function initPdpGalleryVideos() {
    document.querySelectorAll(SELECTOR).forEach(bindSlider);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initPdpGalleryVideos);
  } else {
    initPdpGalleryVideos();
  }

  document.addEventListener('shopify:section:load', initPdpGalleryVideos);

  const observer = new MutationObserver((mutations) => {
    const hasNewPdpSlider = mutations.some((mutation) =>
      Array.from(mutation.addedNodes).some((node) =>
        node.nodeType === Node.ELEMENT_NODE &&
        (node.matches?.(SELECTOR) || node.querySelector?.(SELECTOR))
      )
    );

    if (hasNewPdpSlider) initPdpGalleryVideos();
  });

  if (document.body) {
    observer.observe(document.body, { childList: true, subtree: true });
  }
})();
